import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, QrCode, Share2, Loader2 } from "lucide-react";
import { InviteDialog, looksLikeIdentifier } from "./InviteDialog";
import { shareLink } from "../lib/share";

/**
 * Person-to-person connections ("contatos avulsos").
 * The user's "code" IS their unique identifier: e-mail or phone (like WhatsApp).
 * Reused in the Profile page AND inside the "New conversation" dialog.
 */
export const PersonalConnectBlock = ({ onConnected }) => {
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [redeemCode, setRedeemCode] = useState("");
  const [inviteTarget, setInviteTarget] = useState(null); // e-mail/phone not found -> offer an invite
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [myQrOpen, setMyQrOpen] = useState(false);

  const copyIdentifier = (value) => {
    try {
      navigator.clipboard.writeText(value);
      toast.success(t("copied"));
    } catch (e) { /* ignore */ }
  };

  const redeem = async (e) => {
    e.preventDefault();
    if (!redeemCode.trim()) return;
    setRedeemBusy(true);
    try {
      const { data } = await api.post("/connect/redeem", { code: redeemCode.trim() });
      toast.success(`${t("connected_with")} ${data.user?.name || ""}`);
      setRedeemCode("");
      if (onConnected) onConnected(data.conversation.id);
      else navigate(`/chat/${data.conversation.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "invalid_connect_code") {
        if (looksLikeIdentifier(redeemCode)) setInviteTarget(redeemCode.trim());
        else toast.error(t("invalid_connect_code"));
      }
      else if (detail === "own_code") toast.error(t("own_connect_code"));
      else toast.error(t("something_wrong"));
    } finally { setRedeemBusy(false); }
  };

  return (
    <div data-testid="personal-connect-block">
      <InviteDialog identifier={inviteTarget} kind="connect" onClose={() => { setInviteTarget(null); setRedeemCode(""); }} />
      <div className="rounded-lg border bg-muted/40 p-3 mb-3">
        <p className="text-[11px] text-muted-foreground mb-2">{t("connect_code_explainer")}</p>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground w-14 shrink-0">{t("email")}:</span>
            <span className="text-sm font-semibold text-[#2B3990] truncate" data-testid="my-connect-email">{user?.email}</span>
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2 ml-auto" data-testid="copy-connect-email-btn" onClick={() => copyIdentifier(user?.email)}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          {user?.phone && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground w-14 shrink-0">{t("phone")}:</span>
              <span className="text-sm font-semibold text-[#2B3990]" data-testid="my-connect-phone">{user.phone}</span>
              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 ml-auto" data-testid="copy-connect-phone-btn" onClick={() => copyIdentifier(user.phone)}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
        <Button type="button" variant="outline" size="sm" className="mt-2.5 h-9" data-testid="my-qr-btn" onClick={() => setMyQrOpen(true)}>
          <QrCode className="h-3.5 w-3.5 mr-1.5" /> {t("my_qr_btn")}
        </Button>
      </div>
      {/* Personal QR: scanning it connects the other person INSTANTLY with me */}
      <Dialog open={myQrOpen} onOpenChange={setMyQrOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-sm rounded-2xl p-5 sm:p-6"
          aria-describedby={undefined}
          data-testid="my-qr-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-base text-center">{t("my_qr_title")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 min-w-0">
            <div className="rounded-2xl border bg-white p-3 shadow-sm">
              <QRCodeSVG
                value={`${window.location.origin}/?connect=${encodeURIComponent(user?.email || "")}`}
                size={188}
                level="M"
                data-testid="my-qr-svg"
              />
            </div>
            <div className="w-full min-w-0 text-center">
              <div className="text-sm font-semibold text-[#2B3990] break-all px-2">{user?.email}</div>
              <p className="mt-1.5 text-[11px] text-muted-foreground text-center leading-snug break-words px-2">
                {t("my_qr_hint")}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 w-full pt-1">
              <Button
                type="button" variant="outline" data-testid="my-qr-copy-link"
                className="h-10 w-full min-w-0 px-2 text-xs"
                onClick={() => copyIdentifier(`${window.location.origin}/?connect=${encodeURIComponent(user?.email || "")}`)}
              >
                <Copy className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                <span className="truncate">{t("company_qr_copy_link")}</span>
              </Button>
              <Button
                type="button" data-testid="my-qr-share-link"
                className="h-10 w-full min-w-0 px-2 text-xs"
                onClick={async () => {
                  const link = `${window.location.origin}/?connect=${encodeURIComponent(user?.email || "")}`;
                  const result = await shareLink({ title: "OnSiteComms", text: t("share_invite_text_personal"), url: link });
                  if (result === "copied") toast.success(t("link_copied"));
                  else if (result === "failed") toast.error(t("something_wrong"));
                }}
              >
                <Share2 className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                <span className="truncate">{t("share_link")}</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <form onSubmit={redeem} className="flex items-center gap-2">
        <Input
          data-testid="redeem-connect-code-input"
          placeholder={t("enter_someones_code")}
          value={redeemCode}
          onChange={(e) => setRedeemCode(e.target.value)}
          className="h-10"
        />
        <Button type="submit" size="sm" className="h-10" data-testid="redeem-connect-code-btn" disabled={redeemBusy || !redeemCode.trim()}>
          {redeemBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("connect")}
        </Button>
      </form>
    </div>
  );
};
